import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AppKitAuthContext";
import { authService } from "../services/auth";
import { useScrollLock } from "../hooks/useScrollLock";
import CryptoJS from "crypto-js";
import styles from "../styles/ReferralPopup.module.css";
import { API_BASE_URL } from "../services/api";

interface ReferralPopupProps {
  isVisible: boolean;
  onClose: () => void;
}

interface ReferralRewards {
  unpaidRewardsBNB: string;
  totalRewardsBNB: string;
  paidRewardsBNB: string;
  referredUsers: number;
  canClaim: boolean;
  referralRatio: number;
  isVIP: boolean;
}

const ReferralPopup: React.FC<ReferralPopupProps> = ({ isVisible, onClose }) => {
  const { isAuthenticated, walletAddress } = useAuth();
  const [copied, setCopied] = useState(false);
  const [rewards, setRewards] = useState<ReferralRewards | null>(null);
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimSuccess, setClaimSuccess] = useState(false);

  useScrollLock(isVisible);

  const fetchReferralRewards = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoadingRewards(true);
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/referral/rewards`);

      if (response.ok) {
        const data = await response.json();
        setRewards({
          unpaidRewardsBNB: data.unpaidRewardsBNB,
          totalRewardsBNB: data.totalRewardsBNB,
          paidRewardsBNB: data.paidRewardsBNB,
          referredUsers: data.referredUsers,
          canClaim: data.canClaim,
          referralRatio: data.referralRatio,
          isVIP: data.isVIP,
        });
      }
    } catch (error) {
      console.error("Failed to fetch referral rewards:", error);
    } finally {
      setLoadingRewards(false);
    }
  }, [isAuthenticated]);

  // Fetch referral rewards when popup opens and user is authenticated
  useEffect(() => {
    if (isVisible && isAuthenticated && walletAddress) {
      setClaimSuccess(false); // Reset success message when popup opens
      fetchReferralRewards();
    }
  }, [isVisible, isAuthenticated, walletAddress, fetchReferralRewards]);

  const handleClaimRoyalties = useCallback(async () => {
    if (!isAuthenticated || !rewards?.canClaim || claiming) return;

    setClaiming(true);
    setClaimSuccess(false);

    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/referral/claim`, {
        method: "POST",
      });

      if (response.ok) {
        await response.json(); // Response received successfully
        setClaimSuccess(true);
        // Refresh rewards data to show updated amounts
        setTimeout(() => {
          fetchReferralRewards();
        }, 1000);
      } else {
        const errorData = await response.json();
        console.error("Claim failed:", errorData.message || "Unknown error");
      }
    } catch (error) {
      console.error("Failed to claim royalties:", error);
    } finally {
      setClaiming(false);
    }
  }, [isAuthenticated, rewards?.canClaim, claiming, fetchReferralRewards]);

  if (!isVisible) return null;

  const generateReferralCode = (address: string): string => {
    const md5Hash = CryptoJS.MD5(address.toLowerCase()).toString();
    return md5Hash.substring(0, 6);
  };

  const getReferralLink = (): string => {
    if (!walletAddress) return "";
    const code = generateReferralCode(walletAddress);
    return `https://deathx.fun?r=${code}`;
  };

  const handleCopyLink = async () => {
    const link = getReferralLink();
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
    }
  };

  return (
    <div className={styles.referralOverlay}>
      <div className={styles.referralPopup}>
        <div className={styles.referralHeader}>
          {/* <div className="referral-icon">[%]</div> */}
          <div className={styles.referralTitle}>REFERRAL PROGRAM {rewards?.isVIP ? <span className={styles.vipTag}>VIP</span> : ""}</div>
          <button className={styles.referralClose} onClick={onClose}>
            [X]
          </button>
        </div>

        <div className={styles.referralContent}>
          {!isAuthenticated ? (
            <div className={styles.referralMessage}>
              SHARE DEATHX.FUN AND EARN A % OF BETS ON ALL REFERRALS! SIGN IN TO GET YOUR LINK & REFERRAL %!
            </div>
          ) : (
            <>
              <div className={styles.referralMessage}>
                SHARE YOUR REFERRAL LINK AND EARN {loadingRewards ? "..." : `${rewards?.referralRatio ?? 2}%`} OF TOTAL
                BET AMOUNT ON ALL REFERRALS!
              </div>

              <div className={styles.referralLinkSection}>
                <div className={styles.referralLinkLabel}>YOUR REFERRAL LINK:</div>
                <div className={styles.referralLinkContainer}>
                  <input type="text" value={getReferralLink()} readOnly className={styles.referralLinkInput} />
                  <button onClick={handleCopyLink} className={styles.referralCopyBtn}>
                    {copied ? "COPIED!" : "COPY"}
                  </button>
                </div>
              </div>

              {/* Referral Rewards Section - moved to bottom */}
              <div className={styles.referralRewardsSection}>
                {loadingRewards ? (
                  <div className={styles.referralRewardsLoading}>Loading rewards...</div>
                ) : rewards ? (
                  <>
                    <div className={styles.referralRewardsInfo}>
                      <div className={styles.referralRewardsItem}>
                        <span className={styles.referralRewardsLabel}>Referred users:</span>
                        <span className={styles.referralRewardsValue}>{rewards.referredUsers}</span>
                      </div>
                      <div className={styles.referralRewardsItem}>
                        <span className={styles.referralRewardsLabel}>Available royalties:</span>
                        <span className={styles.referralRewardsValue}>{rewards.unpaidRewardsBNB} BNB</span>
                      </div>
                    </div>

                    {claimSuccess && <div className={styles.referralClaimSuccess}>Royalties claimed successfully!</div>}

                    <button
                      onClick={handleClaimRoyalties}
                      disabled={!rewards.canClaim || claiming}
                      className={`${styles.referralClaimBtn} ${rewards.canClaim ? styles.enabled : styles.disabled}`}
                    >
                      {claiming ? "CLAIMING..." : "CLAIM ROYALTIES"}
                    </button>
                  </>
                ) : (
                  <>
                    <div className={styles.referralRewardsInfo}>
                      <div className={styles.referralRewardsItem}>
                        <span className={styles.referralRewardsLabel}>Referred users:</span>
                        <span className={styles.referralRewardsValue}>0</span>
                      </div>
                      <div className={styles.referralRewardsItem}>
                        <span className={styles.referralRewardsLabel}>Available royalties:</span>
                        <span className={styles.referralRewardsValue}>0.000000 BNB</span>
                      </div>
                    </div>

                    <button disabled className={`${styles.referralClaimBtn} ${styles.disabled}`}>
                      CLAIM ROYALTIES
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralPopup;
